import type { FormEvent } from "react";
import { memo, useRef, useState } from "react";
import { Button, ButtonTypes, IconSize, Input } from "@clintonelec/react-storybook";
import "Components/Auth/LoginPage/LoginPage.less";
import { loginUserAction } from "Data/Redux/Slices/User";
import type { SerializedError } from "@reduxjs/toolkit";
import { NotificationType, notify, notifyError } from "src/Notifications";
import { useAppDispatch } from "Data/Redux/Store";
import { forgotPassword } from "Data/API/Auth";
import { Link, useNavigate } from "react-router-dom";
import { toggleFeatureRenderer } from "Components/Features";
import { Features } from "Data/Utils/Features";
import AuthPage from "Components/Auth/AuthPage";

type LoginFormFields = {
	email: HTMLInputElement;
	password: HTMLInputElement;
} & HTMLFormElement;

type ForgotPasswordFormFields = {
	email: HTMLInputElement;
} & HTMLFormElement;

const LoginPage = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const loginUser = (email: string, password: string) => dispatch(loginUserAction({ email, password }));
	const formRef = useRef<HTMLFormElement>(null);
	const [ submitted, updateSubmitted ] = useState(false);
	const [ forgotPasswordVisible, setForgotPasswordVisible ] = useState(false);

	const handleForgotPasswordClick = () => {
		setForgotPasswordVisible(!forgotPasswordVisible);
	};

	const handleLoginSubmit = (event: FormEvent<LoginFormFields>) => {
		event.preventDefault();

		if (!loginUser) {
			return;
		}

		updateSubmitted(true);

		if (formRef.current?.checkValidity()) {
			loginUser(event?.currentTarget?.email?.value, event?.currentTarget?.password?.value)
				.unwrap()
				.then(() => {
					navigate("/");
				})
				.catch((error: SerializedError) => {
					if (error) {
						notifyError(error);
					} else {
						navigate("/");
					}
				});
		}
	};

	const handleSubmitForgotPassword = (event: FormEvent<ForgotPasswordFormFields>) => {
		event.preventDefault();

		updateSubmitted(true);

		if (formRef.current?.checkValidity()) {
			forgotPassword({ email: event?.currentTarget?.email?.value })
				.then(() => {
					notify(NotificationType.SUCCESS, "Password reset email sent.");
					updateSubmitted(false);
					setForgotPasswordVisible(!forgotPasswordVisible);
				})
				.catch(() => {
					notify(NotificationType.ERROR, "Too many attempts.");
				});
		}
	};

	const loginForm = (
		<form onSubmit={ handleLoginSubmit } ref={ formRef } noValidate>
			<label>Email</label>
			<Input
				name="email"
				wrapClassName="login-input-email"
				required
				noValidate={ !submitted }
				validityMessage="Email is required"
			/>
			<label>Password</label>
			<Input
				name="password"
				wrapClassName="login-input"
				password
				required
				noValidate={ !submitted }
				validityMessage="Password is required"
			/>
			<Button
				id="login-button"
				className="login-button"
				type={ ButtonTypes.PRIMARY }
				htmlType="submit"
				icon={ {
					name: "sign-in",
					size: IconSize.SMALLER
				} }
			>
				Sign In
			</Button>
		</form>
	);

	const forgotPasswordForm = (
		<form ref={ formRef } noValidate onSubmit={ handleSubmitForgotPassword }>
			<label>Email</label>
			<Input
				name="email"
				required
				noValidate={ !submitted }
				validityMessage={ submitted ? "Email is required" : undefined }
			/>
			<Button
				id="login-button"
				className="login-button"
				type={ ButtonTypes.PRIMARY }
				htmlType="submit"
				icon={ {
					name: "envelope",
					size: IconSize.SMALLER
				} }
			>
				Send Recovery Link
			</Button>
		</form>
	);

	const renderRegisterLink = () => {
		if (!toggleFeatureRenderer(Features.REGISTER, true, false) || forgotPasswordVisible) {
			return;
		}

		return (
			<Link className="form-link" to="/register">Don&apos;t have an account?</Link>
		);
	};

	const renderPageAction = () => {
		if (forgotPasswordVisible) {
			return (
				<div className="form-link" onClick={ handleForgotPasswordClick }>Return to Log in page</div>
			);
		}

		return (
			<div className="form-link" onClick={ handleForgotPasswordClick }>Forgot Your Password?</div>
		);
	};

	const formFooter = (
		<div className="form-footer">
			{ renderPageAction() }
			{ renderRegisterLink() }
		</div>
	);

	const pageContent = forgotPasswordVisible ? forgotPasswordForm : loginForm;
	const loginContent = (
		<div className="login-page">
			<div className="logo" />
			{ pageContent }
			{ formFooter }
		</div>
	);

	return <AuthPage form={ loginContent } />;
};

export default memo(LoginPage);
